import { Vue } from 'vue-property-decorator';
import moment from 'moment';

export default Vue.extend({
  props: {
    corporate: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Full list of all tags from GET /tags, used to populate the tag search autocomplete
    allTags: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // ── Donut inline edit ─────────────────────────────────────────────────
      // Tracks which donut number is being edited: null | 'sessionsUsed' | 'maxSessions'
      donutEditing: null,
      // Holds the number currently typed in the donut edit input
      donutEditValue: null,
      // Error message shown inside the donut overlay when the user exceeds max sessions
      donutEditError: '',
      // Controls the donut help dialog (the ? button)
      donutHelpOpen: false,

      // ── Change expiry modal ───────────────────────────────────────────────
      // Controls whether the change expiry dialog is open
      expiryModalOpen: false,
      // The date string typed in the expiry date input (YYYY-MM-DD)
      expiryDraftDate: '',
      // True while the expiry save API call is in flight
      expirySubmitting: false,

      // ── Switch package type modal ─────────────────────────────────────────
      // Controls whether the switch type dialog is open
      switchTypeModalOpen: false,
      // Sessions entered in the switch modal (only used when switching PPU → Bundle)
      switchDraftSessions: null,
      // Quota per person (in sessions) entered in the switch modal
      switchDraftQuota: null,
      // Quota refresh rate selected in the switch modal
      switchDraftRefresh: null,
      // Expiry date entered in the switch modal (YYYY-MM-DD)
      switchDraftExpiry: '',
      // True while the switch type save API call is in flight
      switchTypeSubmitting: false,

      // ── Inline field edit (quota per person and user cap) ─────────────────
      // Tracks which field row is being edited inline: null | 'quota' | 'userCap'
      editingField: null,
      // The number currently typed in the inline edit input
      editValue: null,

      // ── Tag edit mode ─────────────────────────────────────────────────────
      // True while the tag edit mode is open
      tagEditMode: false,
      // Working copy of tag IDs being edited (copied from corporate.tags on open)
      tagEditIds: [],
      // Holds the most recently selected tag from the autocomplete (cleared after adding)
      tagEditSelected: null,
      // True while the tag save API call is in flight
      tagSubmitting: false,
    };
  },
  computed: {
    isBundle() {
      return this.corporate && this.corporate.package_type === 'time';
    },
    glossaryTags() {
      // Render the human-meaningful tag names dynamically. Filters out the
      // per-provider and per-org scaffolding tags (e.g. provider_42,
      // organization_11005, provider_371_c2c_Thailand) — those are access-control
      // identifiers, not categorisation, and dumping them in the glossary
      // would obscure the tags a CS rep actually wants to see.
      // Categories themselves are explained in the glossary text above.
      if (!this.corporate.tags || !this.corporate.tags.length) return [];
      const SCAFFOLDING_PATTERN = /^(provider|organization)_\d+/i;
      return this.corporate.tags
        .map((t) => t && (t.name || t.label))
        .filter((name) => name && !SCAFFOLDING_PATTERN.test(name));
    },
    // ── Donut calculations ────────────────────────────────────────────────

    // Sessions already consumed = (max purchased) minus (remaining in wallet)
    sessionsUsed() {
      const maxS = this.sessionsMax;
      const remS = Math.floor((Number(this.corporate.wallet_time) || 0) / 30);
      return Math.max(maxS - remS, 0);
    },

    // Total sessions purchased (the ceiling shown as the grey number)
    sessionsMax() {
      return Math.floor((Number(this.corporate.max_wallet_time) || 0) / 30);
    },

    // Fixed circumference for a circle of radius 42 (2 × π × 42)
    donutCircumference() {
      return 2 * Math.PI * 42;
    },

    // Controls how much of the teal arc is drawn; reacts live to donutEditValue while editing
    donutOffset() {
      const maxMin = Number(this.corporate.max_wallet_time) || 0;
      const maxS = Math.floor(maxMin / 30);
      if (!maxS) return this.donutCircumference;

      let usedS;
      if (this.donutEditing === 'sessionsUsed' && this.donutEditValue !== null) {
        // Live preview: the typed value IS the sessions used
        usedS = Math.min(Math.max(Number(this.donutEditValue) || 0, 0), maxS);
      } else if (this.donutEditing === 'maxSessions' && this.donutEditValue !== null) {
        // Live preview: new max, sessions used stays the same, arc does not move
        const newMax = Math.max(Number(this.donutEditValue) || 1, 1);
        const remS = Math.floor((Number(this.corporate.wallet_time) || 0) / 30);
        usedS = Math.max(newMax - remS, 0);
        return this.donutCircumference * (1 - Math.min(usedS / newMax, 1));
      } else {
        usedS = this.sessionsUsed;
      }

      const pct = Math.min(usedS / maxS, 1);
      return this.donutCircumference * (1 - pct);
    },

    // ── Switch type modal ─────────────────────────────────────────────────

    // True when all required fields in the switch type modal are filled
    canSubmitSwitchType() {
      if (!this.switchDraftQuota || Number(this.switchDraftQuota) <= 0) return false;
      if (!this.switchDraftRefresh) return false;
      if (!this.switchDraftExpiry) return false;
      if (!this.isBundle) {
        // PPU → Bundle additionally requires a session count
        if (!this.switchDraftSessions || Number(this.switchDraftSessions) <= 0) return false;
      }
      return true;
    },

    // Minimum selectable expiry date — one day after package start, prevents backdating
    minExpiryDate() {
      return this.corporate.package_start ? this.corporate.package_start.slice(0, 10) : null;
    },

    // ── Tags ──────────────────────────────────────────────────────────────

    // All tags from allTags prop that are not already in the current edit selection
    tagsAvailableToAdd() {
      if (!this.allTags || !this.allTags.length) return [];
      const selectedSet = new Set(this.tagEditIds);
      return this.allTags.filter((tag) => !selectedSet.has(tag.id));
    },
  },
  methods: {
    onStatusToggleClick() {
      const newState = !this.corporate.active;
      const verb = newState ? 'activate' : 'deactivate';
      const orgName = this.corporate.name || 'this organisation';

      this.$root
        .$confirm(
          `${verb.charAt(0).toUpperCase()}${verb.slice(1)} organisation`,
          `Are you sure you want to ${verb} <b>${orgName}</b>?`,
          { color: newState ? 'success' : 'error' }
        )
        .then((confirm) => {
          if (confirm) {
            this.$emit('toggle-status');
          }
        });
    },
    blockNonDigitKeys(event) {
      // Allow: digits 0–9, Backspace, Delete, Tab, Escape, Enter, arrow keys
      // for cursor navigation, Home/End, and Ctrl/Cmd combos for copy/paste.
      // Block everything else — including the up/down arrow keys when used
      // for value increment/decrement (handled by preventing the default on
      // ArrowUp/ArrowDown) and any non-digit character key.
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (allowedKeys.includes(event.key)) return;
      if (event.ctrlKey || event.metaKey) return; // allow Ctrl/Cmd shortcuts
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        return;
      }
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    },
    // ── Date formatting ───────────────────────────────────────────────────

    // Formats a raw timestamp string into a readable date e.g. "13 Jun 2026"
    displayDate(val) {
      if (!val) return '—';
      const m = moment(val);
      return m.isValid() ? m.format('D MMM YYYY') : '—';
    },

    // ── Donut inline edit ─────────────────────────────────────────────────

    // Opens the donut edit overlay and pre-fills the input with the current value
    startDonutEdit(field) {
      const maxS = this.sessionsMax;
      const remS = Math.floor((Number(this.corporate.wallet_time) || 0) / 30);
      const usedS = Math.max(maxS - remS, 0);
      this.donutEditValue = field === 'sessionsUsed' ? usedS : maxS;
      this.donutEditError = '';
      this.donutEditing = field;
      this.$nextTick(() => {
        if (this.$refs.donutInput) this.$refs.donutInput.focus();
      });
    },

    // Clears the error message as the user corrects their input (arc updates via donutOffset computed)
    onDonutEditInput() {
      if (this.donutEditError) this.donutEditError = '';
    },

    // Validates the input, shows an inline error if invalid, then emits the correct event
    onDonutEditSave() {
      const val = parseInt(this.donutEditValue);
      if (isNaN(val) || val < 0) return;

      if (this.donutEditing === 'sessionsUsed') {
        const maxS = this.sessionsMax;
        if (val > maxS) {
          // Block save and show error inside the overlay instead of an alert
          this.donutEditError = `max is ${maxS}`;
          return;
        }
        // New wallet = (max - new used) × 30; emit the signed minute delta
        const newWalletTime = (maxS - val) * 30;
        const oldWalletTime = Number(this.corporate.wallet_time) || 0;
        this.$emit('sessions-used-submit', {
          amount: newWalletTime - oldWalletTime,
          type: this.corporate.package_type,
          description: `Sessions used manually adjusted to ${val}`,
          associated_type: 'manual_adjustment',
        });
      } else if (this.donutEditing === 'maxSessions') {
        // Block decrease below current sessions used — reduction of used sessions requires
        // a separate manual_adjustment transaction for a complete audit trail
        if (val < this.sessionsUsed) {
          this.donutEditError = `Cannot go below ${this.sessionsUsed} sessions used. Reduce sessions used first.`;
          return;
        }
        const oldMaxMin = Number(this.corporate.max_wallet_time) || 0;
        const deltaMin = val * 30 - oldMaxMin;
        const newWalletTime = Math.max(0, (Number(this.corporate.wallet_time) || 0) + deltaMin);
        this.$emit('max-sessions-submit', {
          new_max_wallet_time: val * 30,
          new_wallet_time: newWalletTime,
        });
      }

      this.onDonutEditCancel();
    },

    // Closes the donut edit overlay and resets all edit state
    onDonutEditCancel() {
      this.donutEditing = null;
      this.donutEditValue = null;
      this.donutEditError = '';
    },

    // ── Change expiry modal ───────────────────────────────────────────────

    // Pre-fills the draft date from the current expire value and opens the modal
    openExpiryModal() {
      this.expiryDraftDate = this.corporate.expire ? this.corporate.expire.slice(0, 10) : '';
      this.expiryModalOpen = true;
    },

    // Emits the expiry change event and closes the modal
    onExpirySubmit() {
      if (!this.expiryDraftDate) return;
      this.$emit('expiry-submit', { expiry_date: this.expiryDraftDate });
      this.expiryModalOpen = false;
      this.expiryDraftDate = '';
    },

    // ── Switch package type modal ─────────────────────────────────────────

    // Pre-fills draft fields from current org values and opens the switch type modal
    openSwitchTypeModal() {
      this.switchDraftQuota = this.corporate.quota_per_head ? Math.floor(this.corporate.quota_per_head / 30) : null;
      this.switchDraftRefresh = this.corporate.refresh_quota || null;
      this.switchDraftExpiry = this.corporate.expire ? this.corporate.expire.slice(0, 10) : '';
      this.switchDraftSessions = null;
      this.switchTypeModalOpen = true;
    },

    // Builds the full switch payload and emits it; closes and resets the modal
    onSwitchTypeSubmit() {
      if (!this.canSubmitSwitchType) return;
      const oldWalletTime = Number(this.corporate.wallet_time) || 0;
      this.$emit('switch-type-submit', {
        new_package_type: this.isBundle ? 'free' : 'time',
        sessions: !this.isBundle ? Number(this.switchDraftSessions) : null,
        quota_per_head: Number(this.switchDraftQuota) * 30,
        refresh_quota: this.switchDraftRefresh,
        expiry_date: this.switchDraftExpiry,
        // Included so the parent can POST a drain transaction if switching Bundle → PPU
        old_wallet_time: this.isBundle ? oldWalletTime : null,
      });
      this.switchTypeModalOpen = false;
      this.switchDraftSessions = null;
      this.switchDraftQuota = null;
      this.switchDraftRefresh = null;
      this.switchDraftExpiry = '';
    },

    // ── Inline field edit (package name, quota per person and user cap) ───

    // Pre-fills the edit box with the current value, opens edit mode, and focuses the box
    startFieldEdit(field) {
      if (field === 'quota') {
        this.editValue = this.corporate.quota_per_head ? Math.floor(this.corporate.quota_per_head / 30) : 0;
      } else if (field === 'userCap') {
        this.editValue = this.corporate.user_cap || 0;
      } else if (field === 'packageName') {
        // Starts from the current name, or blank when this organisation has none yet
        this.editValue = this.corporate.package || '';
      }
      this.editingField = field;
      // Put the cursor into the box that just appeared, so the user can type immediately
      this.$nextTick(() => {
        if (this.$refs.fieldInput) this.$refs.fieldInput.focus();
      });
    },

    // Converts the session input to minutes where needed and emits the correct event
    onFieldEditSave() {
      // Package name is text, so it saves here before the number checks below run
      if (this.editingField === 'packageName') {
        const name = (this.editValue || '').trim();
        // A blank name would show an empty heading to the customer, so refuse to save it
        if (!name) return;
        this.$emit('package-submit', { package: name });
        this.onFieldEditCancel();
        return;
      }
      const val = parseInt(this.editValue);
      if (isNaN(val) || val < 0) return;
      if (this.editingField === 'quota') {
        this.$emit('quota-submit', { quota_per_head: val * 30 });
      } else if (this.editingField === 'userCap') {
        this.$emit('user-cap-submit', { user_cap: val });
      }
      this.onFieldEditCancel();
    },

    // Closes the inline edit zone without saving
    onFieldEditCancel() {
      this.editingField = null;
      this.editValue = null;
    },

    // Emits the refresh rate change immediately when the dropdown value changes
    onRefreshRateChange(val) {
      this.$emit('refresh-rate-change', { refresh_quota: val });
    },

    // ── Tag edit mode ─────────────────────────────────────────────────────

    // Returns the display name for a tag ID, falling back to corporate.tags if allTags is not loaded
    tagNameById(id) {
      if (this.allTags && this.allTags.length) {
        const tag = this.allTags.find((t) => t.id === id);
        if (tag) return tag.name;
      }
      const fallback = this.corporate.tags && this.corporate.tags.find((t) => t.id === id);
      return fallback ? fallback.name : `Tag ${id}`;
    },

    // Copies current tag IDs into the working array and opens tag edit mode
    initTagEdit() {
      this.tagEditIds = this.corporate.tags ? this.corporate.tags.map((t) => t.id) : [];
      this.tagEditSelected = null;
      this.tagEditMode = true;
    },

    // Removes a tag ID from the working selection
    removeTagFromEdit(id) {
      this.tagEditIds = this.tagEditIds.filter((tagId) => tagId !== id);
    },

    // Adds the selected tag to the working selection and clears the autocomplete
    onTagAddFromSearch(id) {
      if (!id) return;
      if (!this.tagEditIds.includes(id)) {
        this.tagEditIds.push(id);
      }
      this.$nextTick(() => {
        this.tagEditSelected = null;
      });
    },

    // Emits the full new tag ID array to the parent and closes tag edit mode
    onTagEditSave() {
      this.$emit('tag-save', { tag_ids: [...this.tagEditIds] });
      this.tagEditMode = false;
      this.tagEditIds = [];
      this.tagEditSelected = null;
    },

    // Discards all tag edit changes and closes edit mode
    cancelTagEdit() {
      this.tagEditMode = false;
      this.tagEditIds = [];
      this.tagEditSelected = null;
    },
  },
});
